import { memo, useState } from 'react'
import { Head } from 'vite-react-ssg'
import { Link, useNavigate } from 'react-router-dom'
import { AlternateHead } from '@/i18n/AlternateHead'
import { dentaireCopy } from '@/i18n/dentaire'
import { href } from '@/i18n/locale'
import { aboutCopy } from '@/i18n/pages'
import { useLocale } from '@/i18n/useLocale'
import { ClientMediaReviews } from '@/components/ClientMediaReviews'
import ScrollReveal from '@/components/ScrollReveal'
import LogoLoop from '@/components/LogoLoop'
import { VslPlayer } from '@/components/vsl/VslPlayer'
import { MetaPartnerBadge } from '@/components/MetaPartnerBadge'
import { TrustedClinicsMarquee } from '@/components/TrustedClinics'
import { SeoFaqSection } from '@/components/SeoFaqSection'
import { CABINET_PILOTE_COPY } from '@/data/cabinetPiloteOffer'
import { questionsForSchema } from '@/data/dentalFaqs'
import { getResourceArticle, resourceArticlePath } from '@/data/resources'
import { wistiaVideoSchema } from '@/data/seoConstants'

const WISTIA_MEDIA_ID = import.meta.env.VITE_WISTIA_MEDIA_ID ?? 'na1pc11zly'

type BillingCycle = 'monthly' | 'yearly'

function CheckIcon() {
  return (
    <svg className="mt-0.5 size-5 shrink-0 text-fw-violet" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

const ctaClass = 'inline-flex min-h-11 items-center justify-center rounded-xl bg-fw-violet px-8 py-3.5 text-[15px] font-semibold text-white shadow-fw-btn hover:bg-fw-violet-hover'
const ghostCtaClass = 'inline-flex min-h-11 items-center justify-center rounded-xl border border-fw-hairline bg-fw-pure px-8 py-3.5 text-[15px] font-semibold text-fw-ink hover:border-fw-violet-border'

// Describe the service before the video so visitors can assess it without playing media.
const dentalSearchCopy = {
  fr: {
    title: 'Agent IA pour cabinets et centres dentaires | Forteuno Web',
    description: 'Cabinets et centres dentaires au Maroc : accueil WhatsApp, rendez-vous et suivi en français, arabe et darija. Essai gratuit de 7 jours, sans engagement.',
    summary: 'Cabinet Pilote est l’agent IA de Forteuno Web pour les cabinets et centres dentaires au Maroc. Il accueille vos patients sur WhatsApp, Instagram et Messenger, organise les rendez-vous et transmet les demandes à votre équipe en français, arabe ou darija.',
    pricingLink: 'Comparer les tarifs en DH',
    centerQuestion: 'Le service convient-il aussi à un centre dentaire ?',
    centerAnswer: 'Oui. Nous cadrons le fonctionnement du centre avant la configuration : canaux utilisés, volume de demandes, services proposés, agenda et personnes qui reprennent les conversations. Les réponses et les règles de rendez-vous sont validées avec votre équipe. Si votre organisation demande des règles particulières, nous vérifions le périmètre pendant le cadrage plutôt que de supposer qu’un modèle de cabinet individuel suffit.',
    evaluationQuestion: 'Comment savoir si l’essai apporte une valeur à mon cabinet ?',
    evaluationAnswer: 'Pendant les sept jours d’essai, examinez les demandes reçues, les rendez-vous pris et les conversations transférées à votre équipe. Comparez les réponses aux règles validées avec le cabinet : disponibilités, tarifs, langues et situations nécessitant une personne. Les relances et rappels dépendent de la formule choisie. Le nombre de messages ne suffit pas à mesurer la réussite : regardez aussi si les patients arrivent à la bonne prochaine étape et si votre équipe peut reprendre les échanges facilement. Vous décidez ensuite de continuer ou non, sans engagement sur la formule mensuelle.',
  },
  en: {
    title: 'WhatsApp AI Agent for Dentists in Morocco | Forteuno Web',
    description: 'WhatsApp AI for dental practices and centers in Morocco: patient intake, appointments and follow-up in French, Arabic and Darija. Free 7-day trial.',
    summary: 'Cabinet Pilote is Forteuno Web’s AI agent for dental practices and dental centers in Morocco. It welcomes patients on WhatsApp, Instagram and Messenger, organizes appointments and hands requests to your team in French, Arabic or Darija.',
    pricingLink: 'Compare prices in MAD',
    centerQuestion: 'Is the service also suitable for a dental center?',
    centerAnswer: 'Yes. We define how the center works before configuration: channels, request volume, services, calendar and the people who take over conversations. Your team validates replies and appointment rules. If your organization needs specific rules, we check the scope during setup rather than assuming a single-practice template will fit.',
    evaluationQuestion: 'How can I assess whether the trial helps my clinic?',
    evaluationAnswer: 'During the seven-day trial, review incoming requests, booked appointments and conversations handed to your team. Compare replies with the rules your clinic approved: availability, fees, languages and situations needing a person. Reminders and follow-ups depend on the selected plan. Message volume alone does not measure success: check whether patients reach the right next step and whether your team can resume conversations easily. You then decide whether to continue, with no commitment on the monthly plan.',
  },
  ar: {
    title: 'وكيل ذكاء اصطناعي لعيادات ومراكز الأسنان | Forteuno Web',
    description: 'أتمتة استقبال مرضى عيادة الأسنان عبر واتساب وحجز المواعيد بالفرنسية والعربية والدارجة. جرّب Cabinet Pilote مجانًا لمدة 7 أيام دون التزام.',
    summary: 'Cabinet Pilote هو وكيل الذكاء الاصطناعي من Forteuno Web لعيادات ومراكز الأسنان في المغرب. يستقبل المرضى عبر واتساب وإنستغرام ومسنجر، وينظم المواعيد ويحوّل الطلبات إلى فريقكم بالفرنسية أو العربية أو الدارجة.',
    pricingLink: 'مقارنة الأسعار بالدرهم',
    centerQuestion: 'هل تناسب الخدمة مراكز الأسنان أيضًا؟',
    centerAnswer: 'نعم. نحدد طريقة عمل المركز قبل الإعداد: قنوات التواصل وحجم الطلبات والخدمات والأجندة والأشخاص الذين يستأنفون المحادثات. يعتمد فريقكم الردود وقواعد المواعيد. إذا كانت لديكم قواعد خاصة، نراجع نطاقها قبل التشغيل بدل افتراض أن نموذج عيادة واحدة يناسب المركز.',
    evaluationQuestion: 'كيف أقيم فائدة التجربة لعيادتي؟',
    evaluationAnswer: 'خلال التجربة التي تستمر سبعة أيام، راجعوا الطلبات الواردة والمواعيد المحجوزة والمحادثات المحوّلة إلى فريقكم. قارنوا الردود بالقواعد التي اعتمدتها العيادة: المواعيد المتاحة والأسعار واللغات والحالات التي تحتاج إلى شخص. تعتمد التذكيرات والمتابعات على الخطة المختارة. عدد الرسائل وحده لا يقيس النجاح؛ تحققوا أيضًا من وصول المرضى إلى الخطوة المناسبة وقدرة فريقكم على استئناف المحادثات بسهولة. بعد ذلك تقررون الاستمرار أو التوقف، دون التزام في الخطة الشهرية.',
  },
}

export const Dentaire = memo(function Dentaire() {
  const navigate = useNavigate()
  const locale = useLocale()
  const [billing, setBilling] = useState<BillingCycle>('yearly')
  const copy = dentaireCopy[locale]
  const searchCopy = dentalSearchCopy[locale]
  const faqs = [
    ...copy.faqs,
    { question: searchCopy.centerQuestion, answer: searchCopy.centerAnswer },
    { question: searchCopy.evaluationQuestion, answer: searchCopy.evaluationAnswer },
  ]
  const bookingPath = `${href('/discover', locale)}#scheduler`
  const offer = CABINET_PILOTE_COPY[locale]
  const roles = aboutCopy[locale].roles
  const relatedGuideSlugs = ['ai-chatbot-moroccan-darija-dialects', 'premiers-patients-cabinet-medical-maroc', 'pourquoi-agent-ia-medical-a-besoin-sops', 'whatsapp-bloque-24-heures-api-officielle']
  const relatedGuides = relatedGuideSlugs
    .map((slug) => getResourceArticle(slug, locale))
    .filter((article): article is NonNullable<typeof article> => Boolean(article))
  const relatedGuidesLabel = locale === 'ar' ? 'اقرأ أيضا' : locale === 'en' ? 'Related reading' : 'À lire aussi'
  const team = [
    {
      name: 'Mohamed Amine Saada',
      role: roles.amine,
      description: roles.amineDesc,
      href: href('/about/mohamed-amine-saada', locale),
      image: '/images/team/mohamed-amine-saada.webp',
    },
    {
      name: 'Omar Jarrar',
      role: roles.omar,
      description: roles.omarDesc,
      href: null,
      image: '/images/team/team-member-2.webp',
    },
    {
      name: 'Bilal Jarrar',
      role: roles.bilal,
      description: roles.bilalDesc,
      href: null,
      image: '/images/team/team-member-3.webp',
    },
  ]

  const dentaireCanonical = `https://forteunoweb.com${href('/dentaire', locale)}`
  const dentaireVideoSchema = wistiaVideoSchema({
    pageUrl: dentaireCanonical,
    mediaId: WISTIA_MEDIA_ID,
    name: copy.title.replace(' | Forteuno Web', ''),
    description: copy.description,
    thumbnailUrl: `https://fast.wistia.com/embed/medias/${WISTIA_MEDIA_ID}/swatch`,
    uploadDate: '2026-02-13',
    inLanguage: locale === 'ar' ? 'ar-MA' : locale === 'fr' ? 'fr-MA' : 'en',
  })

  return (
    <div className="fw-editorial min-h-screen bg-fw-white">
      <AlternateHead title={searchCopy.title} description={searchCopy.description} canonicalPath={href('/dentaire', locale)} />
      <Head>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `${dentaireCanonical}#webpage`,
            url: dentaireCanonical,
            name: copy.title.replace(' | Forteuno Web', ''),
            inLanguage: locale === 'ar' ? 'ar-MA' : locale === 'fr' ? 'fr-MA' : 'en',
            isPartOf: { '@id': 'https://forteunoweb.com/#website' },
            about: { '@type': 'Service', name: copy.title.replace(' | Forteuno Web', '') },
            mainEntity: questionsForSchema(faqs),
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify(dentaireVideoSchema)}</script>
      </Head>

      <section id="hero-section" className="mx-auto max-w-[1440px] px-6 pb-10 pt-28 md:px-16">
        <div className="mx-auto max-w-5xl text-center">
          <div className="flex items-center justify-center gap-2.5 font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-fw-violet">
            <span className="h-px w-[22px] bg-fw-violet" aria-hidden />
            Cabinet Pilote
          </div>
          <h1 className="mt-4 text-[31px] font-extrabold tracking-[-0.03em] text-fw-ink md:text-[52px]">
            {copy.h1Line1}
            <br />
            {copy.h1Line2}
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-fw-graphite">
            {searchCopy.summary}
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-2xl">
          <VslPlayer
            mediaId={WISTIA_MEDIA_ID}
            accentColor="#6941E8"
            headerText={copy.videoHeader}
            posterAlt={copy.videoAlt}
          />
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <button type="button" onClick={() => navigate(bookingPath)} className={ctaClass}>
            {copy.tryCta}
          </button>
          <a href="#pricing" className="text-sm font-semibold text-fw-violet hover:text-fw-violet-hover">
            {searchCopy.pricingLink} →
          </a>
          <p className="max-w-xl text-center text-base text-fw-graphite">
            {offer.trial} · {offer.commitment} · {offer.capacity}{' '}
            <MetaPartnerBadge label={offer.metaPartner} className="ml-1 align-middle" />
          </p>
        </div>
      </section>

      <TrustedClinicsMarquee locale={locale} />
      <ClientMediaReviews />

      <section id="services" className="mx-auto max-w-[1440px] px-6 py-16 md:px-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">
            {copy.servicesTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-fw-graphite">
            {copy.servicesIntro}
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
          {copy.services.map((service, index) => (
            <ScrollReveal key={service.title} delay={index * 60}>
              <div className="h-full rounded-2xl border border-fw-hairline bg-fw-white p-6 shadow-fw-hairline md:p-8">
                <p className="font-fwmono text-[11px] font-medium uppercase tracking-[0.14em] text-fw-muted">0{index + 1}</p>
                <h3 className="mt-3 text-xl font-bold tracking-[-0.02em] text-fw-ink">{service.title}</h3>
                <ul className="mt-4 space-y-2 text-base text-fw-graphite">
                  {service.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2">
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-fw-violet" aria-hidden />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section id="how-we-work" className="bg-fw-mist-soft px-6 py-16 md:px-16">
        <div className="mx-auto max-w-[1440px]">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">{copy.howTitle}</h2>
            <p className="mt-4 text-base leading-relaxed text-fw-graphite">
              {copy.howIntro}
            </p>
            <button type="button" onClick={() => navigate(bookingPath)} className={`${ctaClass} mt-6`}>
              {copy.howCta}
            </button>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
            {copy.steps.map((item, index) => (
              <ScrollReveal key={item.step} delay={index * 60}>
                <div className="h-full rounded-2xl border border-fw-hairline bg-fw-white p-6 shadow-fw-hairline">
                  <p className="text-[26px] font-bold tracking-[-0.03em] text-fw-ink">
                    {item.step}
                    <span className="text-fw-violet">.</span>
                  </p>
                  <h3 className="mt-3 text-xl font-bold text-fw-ink">{item.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-fw-graphite">{item.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="tool-stack" className="mx-auto max-w-[1440px] px-6 py-16 md:px-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">
            {copy.stackTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-fw-graphite">
            {copy.stackIntro}
          </p>
        </div>
        <div className="mt-8">
          <LogoLoop
            items={[
              { id: 'whatsapp', label: 'WhatsApp', icon: <img src="/icons/whatsapp.svg" alt="WhatsApp" className="size-6" width={24} height={24} loading="lazy" decoding="async" /> },
              { id: 'instagram', label: 'Instagram', icon: <img src="/icons/instagram.svg" alt="Instagram" className="size-6" width={24} height={24} loading="lazy" decoding="async" /> },
              { id: 'messenger', label: 'Messenger', icon: <img src="/icons/messenger.svg" alt="Messenger" className="size-6" width={24} height={24} loading="lazy" decoding="async" /> },
              { id: 'googlecalendar', label: 'Google Calendar', icon: <img src="/icons/googlecalendar.svg" alt="Google Calendar" className="size-6" width={24} height={24} loading="lazy" decoding="async" /> },
              { id: 'calendly', label: 'Calendly', icon: <img src="/icons/calendly.svg" alt="Calendly" className="size-6" width={24} height={24} loading="lazy" decoding="async" /> },
              { id: 'zoom', label: 'Zoom', icon: <img src="/icons/zoom.svg" alt="Zoom" className="size-6" width={24} height={24} loading="lazy" decoding="async" /> },
            ]}
            speedMs={20000}
            className="py-8"
          />
        </div>
      </section>

      <section id="pricing" className="bg-fw-mist-soft px-6 py-16 md:px-16">
        <div className="mx-auto max-w-[1440px]">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">
              {copy.pricingTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-fw-graphite">
              {copy.pricingIntro}
            </p>
          </div>

          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-fw-hairline bg-fw-white p-1">
              <button
                type="button"
                onClick={() => setBilling('monthly')}
                className={`min-h-11 rounded-full px-5 text-sm font-semibold ${billing === 'monthly' ? 'bg-fw-ink text-white' : 'text-fw-graphite'}`}
              >
                {copy.monthly}
              </button>
              <button
                type="button"
                onClick={() => setBilling('yearly')}
                className={`flex min-h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold ${billing === 'yearly' ? 'bg-fw-ink text-white' : 'text-fw-graphite'}`}
              >
                {copy.yearly}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${billing === 'yearly' ? 'bg-fw-violet-electric text-fw-ink' : 'bg-fw-lavender text-fw-violet'}`}>
                  {copy.yearlyBadge}
                </span>
              </button>
            </div>
          </div>

          <div className="mt-10 grid items-stretch gap-6 md:grid-cols-3">
            {copy.tiers.map((tier, index) => {
              const isYearly = billing === 'yearly'
              const monthlyPrice = tier.monthly
              const yearlyMonthlyPrice = tier.yearly
              const monthlyTotal = monthlyPrice * 12
              const yearlyTotal = yearlyMonthlyPrice * 12
              const displayPrice = isYearly ? yearlyTotal : monthlyPrice
              const displayUnit = isYearly ? copy.perYear : copy.perMonth
              const savingsPct = Math.round((1 - tier.yearly / tier.monthly) * 100)

              return (
                <ScrollReveal key={tier.id} delay={index * 60}>
                  <div
                    className={`relative flex h-full flex-col rounded-2xl bg-fw-white p-6 md:p-8 ${
                      tier.highlighted
                        ? 'border-2 border-fw-violet shadow-fw-pricing md:-translate-y-2'
                        : 'border border-fw-hairline shadow-fw-hairline'
                    }`}
                  >
                    {tier.highlighted && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-fw-violet px-4 py-1 text-xs font-bold text-white">
                        {copy.popular}
                      </span>
                    )}

                    <div className="mb-6 text-center">
                      <h3 className="text-2xl font-bold text-fw-ink">{tier.name}</h3>
                      <p className="mt-1 text-base text-fw-graphite">{tier.range}</p>
                    </div>

                    <div className="mb-6 space-y-1 text-center">
                      {isYearly && (
                        <p className="text-sm text-fw-muted line-through">{monthlyTotal} {copy.perYear}</p>
                      )}
                      <p className="text-[40px] font-bold tracking-[-0.03em] text-fw-ink">
                        {displayPrice}
                        <span className="ml-1 text-base font-semibold text-fw-violet">{displayUnit}</span>
                      </p>
                      {isYearly ? (
                        <>
                          <p className="mt-2 inline-block rounded-full border border-fw-lavender-border bg-fw-lavender px-3 py-1 text-sm font-semibold text-fw-ink">
                            {copy.equals(yearlyMonthlyPrice)}
                          </p>
                          <p className="mt-2 text-sm font-semibold text-fw-violet">
                            {copy.billedYearly(savingsPct)}
                          </p>
                          <p className="text-base text-fw-graphite">{copy.onePayment(yearlyTotal)}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-base text-fw-graphite">{copy.billedMonthly}</p>
                          <p className="text-base text-fw-graphite">{copy.orYear(monthlyTotal)}</p>
                        </>
                      )}
                    </div>

                    <ul className="mb-8 flex-1 space-y-2.5 text-base text-fw-graphite">
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2">
                          <CheckIcon />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() => navigate(bookingPath)}
                      className={tier.highlighted ? ctaClass.replace('px-8', 'w-full px-4') : ghostCtaClass.replace('px-8', 'w-full px-4')}
                    >
                      {copy.startTrial}
                    </button>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-base text-fw-graphite">
            {copy.pricingFoot}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-center text-base text-fw-muted">
            {copy.privacyBefore}{' '}
            <Link to={href('/privacy', locale)} className="font-semibold text-fw-violet hover:text-fw-violet-hover">
              {copy.privacy}
            </Link>
          </p>
        </div>
      </section>

      <section id="founder" className="mx-auto max-w-[880px] px-6 py-16 md:px-16">
        <div className="text-center">
          <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">
            {copy.founderTitle}
          </h2>
          <p className="mt-3 text-base text-fw-graphite">
            {copy.founderLede}
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-fw-hairline bg-fw-white p-6 shadow-fw-hairline md:p-10">
          <div className="mb-6 flex items-center gap-5">
            <div className="flex -space-x-3 rtl:space-x-reverse">
              <div className="size-20 shrink-0 overflow-hidden rounded-full border-2 border-fw-white shadow-fw-hairline md:size-24">
                <img src="/images/team/mohamed-amine-saada.webp" alt="Mohamed Amine Saada" className="size-full object-cover" loading="lazy" width="96" height="96" />
              </div>
              <div className="size-20 shrink-0 overflow-hidden rounded-full border-2 border-fw-white shadow-fw-hairline md:size-24">
                <img src="/images/team/khalil.webp" alt="Khalil Jarrar" className="size-full object-cover" loading="lazy" width="96" height="96" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-fw-ink md:text-2xl">{aboutCopy[locale].founderNames}</h3>
              <p className="mt-1 text-sm font-medium text-fw-violet">{copy.founderRole}</p>
            </div>
          </div>
          <div className="space-y-4 text-base leading-relaxed text-fw-graphite">
            <p>
              {copy.founderP1Before}<span className="font-semibold text-fw-ink">{copy.founderP1Emph}</span>{copy.founderP1After}
            </p>
            <p>
              {copy.founderP2Before}<span className="font-semibold text-fw-ink">{copy.founderP2Emph}</span>.
            </p>
          </div>
        </div>
      </section>

      <section id="team" className="mx-auto max-w-[1440px] px-6 pb-8 md:px-16">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">{copy.teamTitle}</h2>
          <p className="mt-3 text-base leading-relaxed text-fw-graphite">
            {copy.teamIntro}
          </p>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {team.map((member) => {
            const inner = (
              <>
                <div className="aspect-[4/5] overflow-hidden rounded-xl border border-fw-hairline bg-fw-mist">
                  <img src={member.image} alt={member.name} className="size-full object-cover" loading="lazy" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-fw-ink">{member.name}</h3>
                <p className="mt-1 text-sm font-medium text-fw-violet">{member.role}</p>
                <p className="mt-2 text-base leading-relaxed text-fw-graphite">{member.description}</p>
              </>
            )
            return member.href ? (
              <Link key={member.name} to={member.href} className="rounded-2xl border border-fw-hairline bg-fw-white p-5 shadow-fw-hairline hover:border-fw-violet-border">
                {inner}
              </Link>
            ) : (
              <div key={member.name} className="rounded-2xl border border-fw-hairline bg-fw-white p-5 shadow-fw-hairline">
                {inner}
              </div>
            )
          })}
        </div>
      </section>

      <SeoFaqSection
        title={copy.faqTitle}
        intro={copy.faqIntro}
        items={faqs}
        className="bg-fw-white"
      />

      {relatedGuides.length > 0 && (
        <section className="mx-auto max-w-[880px] px-6 pb-10 md:px-16">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-fw-hairline-faint pt-6">
            <span className="font-fwmono text-[10px] font-medium uppercase tracking-[0.14em] text-fw-muted">
              {relatedGuidesLabel}
            </span>
            {relatedGuides.map((article) => (
              <Link
                key={article.slug}
                to={resourceArticlePath(article)}
                className="text-sm font-semibold text-fw-violet hover:text-fw-violet-hover"
              >
                {article.title} →
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[880px] px-6 pb-16 text-center md:px-16">
        <h2 className="text-[28px] font-bold tracking-[-0.025em] text-fw-ink md:text-[32px]">
          {copy.closeTitle}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-fw-graphite">
          {copy.closeBody}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button type="button" onClick={() => navigate(bookingPath)} className={ctaClass}>
            {copy.closeCta}
          </button>
          <button type="button" onClick={() => navigate(href('/contact', locale))} className={ghostCtaClass}>
            {copy.contact}
          </button>
        </div>
      </section>
    </div>
  )
})
